import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Copy, Eye, EyeOff } from 'lucide-react';
import { WebhookData } from './types';
import { platformsData } from './platformsData';

interface WebhookEditModalProps {
  webhook: WebhookData | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  videos: Array<{ id: string; title: string }>;
  onVideoChange: (videoId: string) => void;
  onTokenChange: (token: string) => void;
  onCopyUrl: () => void;
  onSave: () => void;
}

export function WebhookEditModal({
  webhook,
  open,
  onOpenChange,
  videos,
  onVideoChange,
  onTokenChange,
  onCopyUrl,
  onSave
}: WebhookEditModalProps) {
  useLanguage();
  const [showToken, setShowToken] = useState(false);

  if (!webhook) return null;

  const platformData = platformsData.find(p => p.id === webhook.platform);
  const availableVideos = videos;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[90vw] max-w-[560px] rounded-xl p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {platformData?.logo && (
              <img src={platformData.logo} alt={platformData.name} className="h-6 w-auto object-contain" />
            )}
            <span>{translateInterfaceText("Configurar")}</span>
          </DialogTitle>
          <DialogDescription>{translateInterfaceText("Selecione o vídeo e informe a chave da plataforma para receber conversões.")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Vídeo Select */}
          <div onClick={(e) => e.stopPropagation()}>
            <label className="text-sm font-medium mb-1.5 block text-foreground">
              {translateInterfaceText("Vídeo")}</label>
            <Select value={webhook.selectedVideoId} onValueChange={onVideoChange}>
              <SelectTrigger className="h-11">
                <SelectValue placeholder={translateInterfaceText("Selecione um vídeo")} />
              </SelectTrigger>
              <SelectContent>
                {availableVideos.length === 0 ? (
                  <div className="px-2 py-1.5 text-sm text-muted-foreground">
                    {translateInterfaceText("Nenhum vídeo disponível. Faça upload de um vídeo primeiro.")}</div>
                ) : (
                  availableVideos.map(video => (
                    <SelectItem key={video.id} value={video.id}>
                      {video.title}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Token da plataforma */}
          <div onClick={(e) => e.stopPropagation()}>
            <label className="text-sm font-medium mb-1.5 block text-foreground">
              {webhook.platform === 'payt' ? translateInterfaceText("Chave de integração Payt (integration_key)") : translateInterfaceText("Token da plataforma")}
            </label>
            <div className="relative">
              <Input
                placeholder={webhook.platform === 'payt' ? translateInterfaceText("Cole a integration_key configurada na Payt") : translateInterfaceText("Cole aqui o token gerado pela plataforma")}
                type={showToken ? 'text' : 'password'}
                value={webhook.platformToken || ''}
                onChange={(e) => onTokenChange(e.target.value)}
                className="h-11 rounded-md pr-10"
              />
              <button
                type="button"
                onClick={() => setShowToken(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground focus:outline-none"
              >
                {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {webhook.platform === 'payt' && (
            <p className="text-xs text-muted-foreground">
              {translateInterfaceText("Na Payt, cadastre esta URL para o evento de pagamento aprovado e use a mesma chave de integração.")}</p>
          )}

          {/* URL Webhook */}
          <div onClick={(e) => e.stopPropagation()}>
            <label className="text-sm font-medium mb-1.5 block text-foreground">
              {translateInterfaceText("URL Webhook")}</label>
            <div className="flex items-center gap-2">
              <Input
                value={webhook.status === 'creation' ? 'Salve o tracking para liberar a URL' : webhook.url}
                readOnly
                title={webhook.status !== 'creation' ? webhook.url : ''}
                className="bg-muted h-11 text-[13px] text-gray-500 truncate"
              />
              {webhook.status !== 'creation' && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCopyUrl();
                  }}
                  className="h-11 w-11 flex-shrink-0"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="pt-2">
          <Button
            className="w-full h-11 bg-[#2563EB] hover:bg-[#1d4ed8] text-white"
            onClick={onSave}
            disabled={!webhook.selectedVideoId || !webhook.platformToken}
          >
            {translateInterfaceText("Salvar Tracking")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
